<template>
  <div class="shop-selections">
    <div class="shop-selections__actions">
      <CheckboxComponent v-model="showDefault" :label="$t('showDefaultSelection')" />
    </div>
    <div
      v-for="{ id, label, subcategories } in categories"
      :key="id"
      class="shop-selections__items"
    >
      <h2>{{ label }}</h2>

      <div v-for="item in subcategories" :key="item.id" class="shop-selections__item">
        <div data-part="thumbnail">
          <img v-if="item.thumbnail" data-part="product-thumbnail" :src="item.thumbnail" />
          <Icon v-else icon="fa-pro-light:box-alt" size="lg" />

          <img v-if="item.variant" data-part="variant-thumbnail" :src="item.variant.thumbnail" />
        </div>
        <div data-part="details">
          <div data-part="product">{{ item.product }}</div>
          <div v-if="item.variant" data-part="variant">{{ item.variant.label }}</div>
          <div data-part="subcategory">{{ item.label }}</div>
        </div>
        <div data-part="total">
          <div v-if="item.isDefault">{{ $t('included') }}</div>
          <div v-else-if="[null, undefined].includes(item.cost)">{{ $t('priceOnRequest') }}</div>
          <div v-else>{{ Currency(item.total) }}</div>
          <div v-if="item.quantity" data-part="cost">
            {{ item.quantity }} × {{ Currency(item.cost) }}
          </div>
        </div>

        <PropertyChanges
          :value="item.referencePropertyChanges"
          data-part="property-changes"
          variant="delta"
        />
      </div>
    </div>
    <Alert v-if="!categories.length" class="shop-selections__no-upgrades">
      {{ $t('noUpgradesSelected') }}
    </Alert>
  </div>
</template>

<script>
import { path } from 'ramda'
import { ConditionFilter, toArray, getItemSum } from '../utils'
import PropertyChanges from '../components/PropertyChanges'
import Icon from '@kvass/vue2-icon'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { AlertComponent as Alert } from 'vue-elder-alert'

const get = (paths, value) =>
  toArray(paths)
    .map(k => path(k.split('.'), value))
    .find(Boolean)

export default {
  data() {
    return {
      showDefault: true,
    }
  },
  props: {
    shop: Object,
  },
  computed: {
    categories() {
      return this.shop.categories
        .filter(category => ConditionFilter(category, this.shop))
        .map(({ id, label, categories }) => {
          let subcategories = categories.filter(
            c => ConditionFilter(c, this.shop) && c.items.find(item => item.selected),
          )
          if (!subcategories.length) return
          if (!this.showDefault)
            subcategories = subcategories.filter(
              ({ items }) => !items.find(({ selected, isDefault }) => selected && isDefault),
            )

          return {
            id,
            label,
            subcategories: subcategories.map(({ id, label, items }) => {
              const selection = items.find(({ selected }) => selected)

              const base = {
                id,
                label,
                thumbnail: get(['product.thumbnail.url', 'product.images.0.url'], selection),
                total: getItemSum(selection),
                cost: get('product.gross', selection),
                product: get('product.name', selection),
                isDefault: get('isDefault', selection),
                referencePropertyChanges: get('referencePropertyChanges', selection),
              }

              if (
                get('variant.value', selection) &&
                get('product.variants.items.length', selection)
              ) {
                const variant = selection.product.variants.items.find(
                  ({ internalId: id }) => selection.variant.value === id,
                )
                base.variant = {
                  label: variant.name,
                  thumbnail: variant.image.url,
                  category: selection.variant.category,
                }
              }

              if (get('quantity.enabled', selection)) base.quantity = selection.quantity.value || 1

              return base
            }),
          }
        })
        .filter(c => c && c.subcategories.length)
    },
  },
  methods: {
    Currency(value) {
      return new Intl.NumberFormat('nb-NO', {
        style: 'currency',
        currency: this.shop.metadata.currency,
        currencyDisplay: 'narrowSymbol',
        trailingZeroDisplay: 'stripIfInteger',
      }).format(value)
    },
  },
  created() {
    this.showDefault = !path(['metadata', 'hideDefaultOnSummary'], this.shop)
  },
  components: {
    Alert,
    CheckboxComponent,
    PropertyChanges,
    Icon,
  },
}
</script>

<style lang="scss">
.shop-selections {
  mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    transparent calc((var(--shop-spacing) * 3.5)),
    black calc((var(--shop-spacing) * 5)),
    black calc(100% - (var(--shop-spacing) * 2)),
    transparent 100%
  );

  &__item {
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto 1fr;
    grid-template-areas: 'thumbnail details total' 'thumbnail details property-changes';

    @container (max-width: 500px) {
      grid-template-columns: auto 1fr;
      grid-template-areas: 'thumbnail details' 'thumbnail property-changes' 'thumbnail total';

      &:has([data-part='property-changes']) {
        grid-template-columns: 1fr;
        grid-template-areas: 'thumbnail' 'details' 'property-changes' 'total';
      }
    }

    gap: 0.5rem 1.5rem;

    $spacing: 1rem;
    padding: $spacing $spacing * 1.5 $spacing $spacing;
    border: 1px solid var(--border-color);

    [data-part='thumbnail'] {
      $size: 100px;

      width: $size;
      height: $size;
      position: relative;

      border-radius: var(--border-radius);
      overflow: clip;

      &:has(svg) {
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: rgba(black, 0.05);

        svg {
          color: rgba(black, 0.3);
        }
      }
    }

    [data-part='product-thumbnail'] {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    [data-part='details'] {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    [data-part='product'] {
      // font-size: 1.1em;
    }

    [data-part='variant'] {
      margin-top: 2px;
      color: rgba(black, 0.5);
    }

    [data-part='property-changes'] {
      font-size: 0.8em;

      border-top: 1px solid var(--border-color);
      padding-top: 0.5rem;
    }

    [data-part='variant-thumbnail'] {
      position: absolute;

      $offset: 3px;
      bottom: $offset;
      right: $offset;

      width: 30%;
      aspect-ratio: 1;
      object-fit: contain;

      border: 1px solid var(--border-color);
      background-color: white;
      border-radius: calc(var(--border-radius) - #{$offset});
    }

    [data-part='subcategory'] {
      margin-top: 0.7rem;
      padding-top: 0.5rem;
      color: rgba(black, 0.5);
      border-top: 1px solid rgba(black, 0.1);
    }

    [data-part='total'] {
      text-align: end;
    }

    [data-part='cost'] {
      opacity: 0.5;
    }

    &:first-of-type {
      border-top-left-radius: calc(var(--border-radius) * 2);
      border-top-right-radius: calc(var(--border-radius) * 2);
    }

    &:last-of-type {
      border-bottom-left-radius: calc(var(--border-radius) * 2);
      border-bottom-right-radius: calc(var(--border-radius) * 2);
    }

    & + & {
      border-top: none;
    }
  }

  &__items {
    container-type: inline-size;

    & + & {
      margin-top: var(--shop-spacing);
    }
  }

  $components: 'thumbnail', 'subcategory', 'total', 'details', 'property-changes';

  @each $component in $components {
    [data-part='#{$component}'] {
      grid-area: #{$component};
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__no-upgrades {
    margin: 2rem auto;
    max-width: 350px;
    font-size: 0.9em;
    .alert__content {
      justify-content: center;
    }
  }
}
</style>
